import { getMyInformation } from '@apis/api/auth';
import {
  getStudentInfo,
  getStudentList,
  getTeacherPageList,
  GetTeacherPageListQuery,
} from '@apis/api/dashboard';
import { SchoolInfo } from '@apis/api/school';
import { getFilteringListApi } from '@apis/api/teacher/attendance';
import {
  StudentStatus,
  TeacherPosition,
  TeacherStatus,
  UserType,
} from '@asset/enum/UserEnum';
import { UserInfo } from '@asset/type/userType';
import { loginUserInfoState, selectedStudentIdAtom } from '@atoms/states';
import { useUserStore } from '@store/user';
import _ from 'lodash';
import { Settings } from 'luxon';
import { useInfiniteQuery, useQuery } from 'react-query';
import { useRecoilState } from 'recoil';

export interface StudentInfo {
  id: number;
  type: UserType;
  name: string;
  timezone: string;
  language: string;
  grade: string;
  school: SchoolInfo | null;
}
const getStudentData = async (studentId: number): Promise<StudentInfo> => {
  const studentInfoData = await getStudentInfo(studentId);
  if (!_.isNil(studentInfoData)) {
    return {
      id: studentId,
      type: UserType.STUDENT,
      name: studentInfoData.name,
      timezone: studentInfoData.timezone,
      language: studentInfoData.language,
      grade: studentInfoData.grade,
      school: studentInfoData.school,
    };
  } else {
    return null;
  }
};

export function useSelectedStudentInfoQuery() {
  const [selectedStudentId] = useRecoilState(selectedStudentIdAtom);

  return useQuery(
    ['GET_STUDENT_INFO_QUERY', selectedStudentId],
    async () => {
      const studentData = await getStudentData(selectedStudentId);
      Settings.defaultZone = studentData.timezone;
      return studentData;
    },
    {
      enabled: Boolean(selectedStudentId),
      cacheTime: 0,
      staleTime: 1 * 60 * 1000,
      keepPreviousData: true,
    },
  );
}

export function useUserInfoQuery() {
  // TODO: 하위 호환을 위해 당분간 남겨두지만 추후 삭제
  const [userLegacy, setUserLegacy] =
    useRecoilState<UserInfo>(loginUserInfoState);
  const { user, logIn, isLoggedIn } = useUserStore();
  return useQuery(
    ['GET_USER_INFO_QUERY', user?.userId],
    async () => {
      // TODO: zustand의 store로 통합하기 위해서 recoil 쪽은 null 세팅해 준다.
      setUserLegacy(null);
      if (!userLegacy?.id && !isLoggedIn()) {
        return null;
      }
      let userResult = user;
      if (!userResult?.id) {
        const userResponse = await getMyInformation();
        if (userResponse.success) {
          userResult = userResponse.data;
        } else {
          return null;
        }
      }
      if (userResult?.type === UserType.STUDENT) {
        const studentData = await getStudentData(userResult.id);
        userResult = {
          userId: userResult.userId,
          avatarName: userResult.avatarName,
          ...studentData,
        };
      }
      logIn(userResult);
      return userResult;
    },
    {
      staleTime: user?.userId ? 5 * 60 * 1000 : 0,
    },
  );
}

export const studentQueryKeys = {
  all: ['STUDENT'] as const,
  lists: () => [...studentQueryKeys.all, 'LISTS'] as const,
  infinite: (options: {
    pageSize?: number;
    search?: string;
    status?: string;
    teacherIds?: number[];
  }) => [...studentQueryKeys.lists(), 'INFINITE', options] as const,

  details: () => [...studentQueryKeys.all, 'DETAILS'],
  detail: (studentId: number) =>
    [...studentQueryKeys.details(), studentId] as const,
  detailByIds: (ids: number[]) =>
    [...studentQueryKeys.details(), { ids }] as const,

  extras: () => [...studentQueryKeys.all, 'EXTRAS'] as const,
  extra: (studentId: number) =>
    [...studentQueryKeys.extras(), studentId] as const,
  extraByIds: (ids: number[]) =>
    [...studentQueryKeys.extras(), { ids }] as const,
};

export const studentStatusEventQueryKeys = {
  all: ['STUDENT_STATUS_EVENT'] as const,
  list: (studentId: number) => ['LIST', studentId] as const,
};

export const useInfiniteStudentsQuery = (
  options: {
    pageSize?: number;
    search?: string;
    status?: StudentStatus;
    teacherIds?: number[];
  } = {},
) => {
  return useInfiniteQuery(
    studentQueryKeys.infinite(options),
    async ({ pageParam = 1 }) => {
      const response = await getStudentList({
        page: pageParam,
        ...options,
      });

      if (response.success) {
        return {
          students: response.students,
          pagination: response.pagination,
          nextPage: response.pagination.nextPage ? pageParam + 1 : undefined,
        };
      } else {
        console.error('Failed to fetch student list');
        return { students: [], pagination: {}, nextPage: undefined };
      }
    },
    {
      getNextPageParam: (lastPage) => {
        return lastPage.nextPage;
      },
    },
  );
};

export const teacherQueryKeys = {
  all: ['TEACHER'] as const,
  lists: () => [...teacherQueryKeys.all, 'LISTS'] as const,
  list: (options: GetTeacherPageListQuery) =>
    [...teacherQueryKeys.lists(), 'LIST', { ...options }] as const,
};

export const useActiveTeacherListQuery = (positionList?: TeacherPosition[]) => {
  const parameters = {
    pageType: 'side-bar',
    statusList: [TeacherStatus.ACTIVE],
    positionList: positionList,
  } as GetTeacherPageListQuery;
  return useQuery(
    teacherQueryKeys.list(parameters),
    async () => await getTeacherPageList(parameters),
  );
};

export const totalTeacherQueryKey = {
  all: ['GET_TOTAL_TEACHER_LIST'] as const,
};

export const useTotalTeacherListQuery = () => {
  return useQuery(totalTeacherQueryKey.all, async () => {
    const response = await getFilteringListApi();
    return response.teacherList;
  });
};

export const useStudentInfoQuery = (studentId: number) =>
  useQuery(
    studentQueryKeys.detail(studentId),
    async () => {
      const studentInfoData = await getStudentInfo(studentId);
      return studentInfoData;
    },
    { enabled: !_.isNil(studentId) },
  );
